import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiQuery } from '../api/hooks';
import type {
  AppointmentPageResponse,
  AppointmentResponse,
  AppointmentStatus,
  AppointmentSummaryResponse,
  BranchResponse,
  ServiceResponse,
  StaffMemberResponse,
} from '../api/types';
import { AsyncSection } from '../components/AsyncSection';
import { useChartsShown } from '../components/charts/useChartsShown';
import { PageHeader } from '../components/PageHeader';
import { ErrorState, LoadingState } from '../components/states/StateViews';
import { useAuthStore } from '../auth/authStore';
import { shortDate } from '../i18n/format';
import { useLocalisedName } from '../i18n/localisedName';
import { AppointmentsOverview } from './insight/AppointmentsOverview';
import { AppointmentDetails } from './scheduling/AppointmentDetails';
import { clinicDay, clinicTime, clinicToday, shiftDay } from './scheduling/clinicTime';

const PAGE_SIZE = 50;

const STATUSES: AppointmentStatus[] = [
  'BOOKED',
  'CONFIRMED',
  'ARRIVED',
  'IN_PROGRESS',
  'COMPLETED',
  'NO_SHOW',
  'CANCELLED',
];

/**
 * Every appointment over a range, newest first (CR-29).
 *
 * The day board answers "what is happening today at this branch"; this answers everything else —
 * last week's no-shows, a doctor's month, what was cancelled at the other branch — a page at a time.
 * Each row opens the same Appointment details as the board and the patient profile (CR-28).
 *
 * Someone who works at one branch is not offered a branch choice: the server answers for their
 * branch only (CR-17), so the picker would only offer an error. The doctor filter needs the staff
 * list, which reception does not hold, so it is offered only to those who do.
 *
 * Above the list, the same search as charts (CR-33): how the range went, when and where. They can
 * be folded away, and pressing a bar sets the matching filter.
 */
export function AppointmentsPage() {
  const { t, i18n } = useTranslation();
  const localised = useLocalisedName();
  const clinicId = useAuthStore((state) => state.activeClinicId);
  const permissions = useAuthStore((state) => state.permissions);
  const confined = useAuthStore(
    (state) =>
      state.principal?.memberships?.find((membership) => membership.clinicId === state.activeClinicId)?.branchId ??
      null,
  );

  const [to, setTo] = useState(clinicToday());
  const [from, setFrom] = useState(shiftDay(clinicToday(), -30));
  const [branchId, setBranchId] = useState('');
  const [employmentId, setEmploymentId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [opened, setOpened] = useState<AppointmentResponse | null>(null);
  const [chartsShown, showCharts] = useChartsShown('appointments');

  const branches = useApiQuery<BranchResponse[]>(
    queryKeys.branches(clinicId ?? ''),
    `/clinics/${clinicId}/branches`,
    { enabled: Boolean(clinicId) && !confined && permissions.has('BRANCH_READ') },
  );
  const staff = useApiQuery<StaffMemberResponse[]>(
    queryKeys.staff(clinicId ?? ''),
    `/clinics/${clinicId}/staff`,
    { enabled: Boolean(clinicId) && permissions.has('STAFF_READ') },
  );
  const services = useApiQuery<ServiceResponse[]>(
    queryKeys.services(clinicId ?? ''),
    `/clinics/${clinicId}/catalog/services`,
    { enabled: Boolean(clinicId) && permissions.has('CATALOG_READ') },
  );

  // The filters alone, which the charts are drawn from; the list adds its page to them.
  const filters = new URLSearchParams({ from, to });
  if (branchId) {
    filters.set('branchId', branchId);
  }
  if (employmentId) {
    filters.set('employmentId', employmentId);
  }
  if (serviceId) {
    filters.set('serviceId', serviceId);
  }
  if (status) {
    filters.set('status', status);
  }
  const filtered = filters.toString();
  const search = `${filtered}&page=${page}&size=${PAGE_SIZE}`;
  const validRange = Boolean(from && to) && from <= to;

  const found = useApiQuery<AppointmentPageResponse>(
    queryKeys.appointmentSearch(clinicId ?? '', search),
    `/clinics/${clinicId}/appointments?${search}`,
    { enabled: Boolean(clinicId) && validRange },
  );
  const summary = useApiQuery<AppointmentSummaryResponse>(
    queryKeys.appointmentSummary(clinicId ?? '', filtered),
    `/clinics/${clinicId}/appointments/summary?${filtered}`,
    { enabled: Boolean(clinicId) && validRange && chartsShown, keepPrevious: true },
  );

  if (!clinicId) {
    return <PageHeader title={t('nav.items.appointments')} description={t('home.noClinic')} />;
  }

  const doctors = (staff.data ?? []).filter((member) => member.role === 'DOCTOR' && member.id);
  const branchName = (id: string | undefined) => {
    const branch = (branches.data ?? []).find((candidate) => candidate.id === id);
    return branch ? localised.name(branch, branch.code) : '—';
  };
  const showBranch = !confined && (branches.data ?? []).length > 1;

  // Every filter change starts again from the first page; a page 3 of a different question is noise.
  const refilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  return (
    <>
      <PageHeader
        title={t('nav.items.appointments')}
        description={t('appointments.description')}
        actions={
          <button type="button" className="button button--ghost" onClick={() => showCharts(!chartsShown)}>
            {chartsShown ? t('overview.hide') : t('overview.show')}
          </button>
        }
      />

      <div className="toolbar" style={{ flexWrap: 'wrap' }}>
        <label className="field">
          <span className="field__label">{t('appointments.from')}</span>
          <input
            type="date"
            className="field__input"
            value={from}
            max={to}
            onChange={(event) => refilter(() => setFrom(event.target.value))}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('appointments.to')}</span>
          <input
            type="date"
            className="field__input"
            value={to}
            min={from}
            onChange={(event) => refilter(() => setTo(event.target.value))}
          />
        </label>
        {!confined && (branches.data ?? []).length > 1 ? (
          <label className="field">
            <span className="field__label">{t('appointments.branch')}</span>
            <select
              className="field__input"
              value={branchId}
              onChange={(event) => refilter(() => setBranchId(event.target.value))}
            >
              <option value="">{t('appointments.allBranches')}</option>
              {(branches.data ?? []).map((branch) => (
                <option key={branch.id} value={branch.id}>
                  {localised.name(branch, branch.code)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {doctors.length > 0 ? (
          <label className="field">
            <span className="field__label">{t('appointments.doctor')}</span>
            <select
              className="field__input"
              value={employmentId}
              onChange={(event) => refilter(() => setEmploymentId(event.target.value))}
            >
              <option value="">{t('appointments.allDoctors')}</option>
              {doctors.map((doctor) => (
                <option key={doctor.id} value={doctor.id}>
                  {doctor.displayName}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {(services.data ?? []).length > 0 ? (
          <label className="field">
            <span className="field__label">{t('appointments.service')}</span>
            <select
              className="field__input"
              value={serviceId}
              onChange={(event) => refilter(() => setServiceId(event.target.value))}
            >
              <option value="">{t('appointments.allServices')}</option>
              {(services.data ?? []).map((service) => (
                <option key={service.id} value={service.id}>
                  {localised.name(service, service.code)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="field">
          <span className="field__label">{t('appointments.status')}</span>
          <select
            className="field__input"
            value={status}
            onChange={(event) => refilter(() => setStatus(event.target.value))}
          >
            <option value="">{t('appointments.allStatuses')}</option>
            {STATUSES.map((option) => (
              <option key={option} value={option}>
                {t(`board.status.${option}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {!validRange ? <p className="field__hint">{t('appointments.badRange')}</p> : null}

      {chartsShown && validRange ? (
        summary.data && (summary.data.total ?? 0) > 0 ? (
          <AppointmentsOverview
            summary={summary.data}
            from={from}
            to={to}
            today={clinicToday()}
            selected={{ status, serviceId, branchId, employmentId }}
            onFilter={{
              status: (value) => refilter(() => setStatus(value)),
              serviceId:
                (services.data ?? []).length > 0 ? (value) => refilter(() => setServiceId(value)) : undefined,
              branchId:
                !confined && (branches.data ?? []).length > 1
                  ? (value) => refilter(() => setBranchId(value))
                  : undefined,
              employmentId: doctors.length > 0 ? (value) => refilter(() => setEmploymentId(value)) : undefined,
            }}
            showBranches={!confined}
          />
        ) : summary.isPending ? (
          <LoadingState rows={3} />
        ) : summary.error ? (
          <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />
        ) : null
      ) : null}

      <AsyncSection
        query={found}
        skeletonRows={8}
        isEmpty={(data) => (data.items ?? []).length === 0}
        emptyTitle={t('appointments.emptyTitle')}
        emptyDescription={t('appointments.emptyDescription')}
      >
        {(data) => {
          const total = data.total ?? 0;
          const first = page * PAGE_SIZE + 1;
          const last = page * PAGE_SIZE + (data.items ?? []).length;
          return (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col">{t('appointments.date')}</th>
                      <th scope="col">{t('appointments.time')}</th>
                      <th scope="col">{t('appointments.patient')}</th>
                      <th scope="col">{t('appointments.service')}</th>
                      <th scope="col">{t('appointments.doctor')}</th>
                      {showBranch ? <th scope="col">{t('appointments.branch')}</th> : null}
                      <th scope="col">{t('appointments.status')}</th>
                      <th scope="col">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.items ?? []).map((appointment) => (
                      <tr key={appointment.id}>
                        <td>{shortDate(clinicDay(appointment.startsAt), i18n.language)}</td>
                        <td className="ltr">
                          {clinicTime(appointment.startsAt)} – {clinicTime(appointment.endsAt)}
                        </td>
                        <td>{appointment.patientName ?? '—'}</td>
                        <td>
                          {localised.name({ nameEn: appointment.serviceNameEn, nameAr: appointment.serviceNameAr })}
                          {appointment.areaNameEn || appointment.areaNameAr
                            ? ` · ${localised.name({ nameEn: appointment.areaNameEn, nameAr: appointment.areaNameAr })}`
                            : ''}
                        </td>
                        <td>{appointment.doctorName ?? '—'}</td>
                        {showBranch ? <td>{branchName(appointment.branchId)}</td> : null}
                        <td>{t(`board.status.${appointment.status ?? 'BOOKED'}`)}</td>
                        <td>
                          <button
                            type="button"
                            className="button button--ghost button--compact"
                            onClick={() => setOpened(appointment)}
                          >
                            {t('appointment.details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="toolbar" style={{ marginBlockStart: 'var(--space-3)', alignItems: 'center' }}>
                <span className="field__hint">{t('appointments.showing', { first, last, total })}</span>
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  {t('appointments.previous')}
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  disabled={last >= total}
                  onClick={() => setPage(page + 1)}
                >
                  {t('appointments.next')}
                </button>
              </div>
            </>
          );
        }}
      </AsyncSection>

      {opened ? <AppointmentDetails clinicId={clinicId} appointment={opened} onClose={() => setOpened(null)} /> : null}
    </>
  );
}
